import { AlertTriangleIcon } from "lucide-react";
import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

type ErrorBoundaryState = { error: Error | null };

export class WidgetErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ReturnWidget] Error boundary caught:", error, info.componentStack);
    // AggregateError (thrown by Jotai's flushCallbacks) hides the real cause(s) in `.errors`.
    // Unwrap them so we see the actual throw, not just "AggregateError".
    if (error instanceof AggregateError) {
      error.errors.forEach((e, i) => {
        console.error(
          `[ReturnWidget] AggregateError cause #${i}:`,
          e,
          (e as Error)?.stack,
        );
      });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <WidgetErrorFallback
          error={this.state.error}
          onReset={() => this.setState({ error: null })}
        />
      );
    }
    return this.props.children;
  }
}

function WidgetErrorFallback({ error, onReset }: { error: Error; onReset: () => void }) {
  const navigate = useNavigate();
  const { shopId } = useParams<{ shopId: string }>();
  const { t } = useTranslation("return-widget-v3");

  const handleReset = () => {
    onReset();
    // Navigate to the widget root (/return-widget/:shopId) to avoid leaving the widget
    // context entirely when there's no prior history entry within the app. Read shopId
    // from the router (not window.location.pathname) so it works under HashRouter too.
    navigate(`/return-widget/${shopId}`);
  };

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
      <AlertTriangleIcon className="size-10 text-destructive" />
      <div className="flex flex-col gap-1">
        <p className="font-sans text-sm font-medium">{t("common.error.title")}</p>
      </div>
      <button
        onClick={handleReset}
        className="font-content text-sm text-primary underline underline-offset-4 cursor-pointer"
      >
        {t("common.back")}
      </button>
    </div>
  );
}
